import { EVALUATION_TOOL } from '../../components/AutomationStudio/constant';
import { getReplayStepDisplayDescription } from '../../components/AutomationStudio/utils/stepExecutionDisplay';
import { getActiveFlatSteps } from './planUtils';
import { RUN_STATUS } from './constants';

const RECORDING_SAVE_STATUS = new Set([
    'pending',
    'completed',
    'failed',
    'failed_by_evaluation',
    'success',
]);

function isEvaluationStep(step) {
    return step?.tool === EVALUATION_TOOL;
}

function normalizePassedValue(value) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
}

/**
 * Resolve evaluation / recording verdict from step fields (including args.passed).
 */
export function getRecordingStepPassed(step) {
    const candidates = [
        step?.passed,
        step?.args?.passed,
        step?.arguments?.passed,
        step?.details?.passed,
        step?.result?.passed,
        step?.result?.result?.passed,
        step?.executionGroup?.mainAction?.passed,
        step?.executionGroup?.mainAction?.args?.passed,
        step?.executionGroup?.mainAction?.details?.passed,
    ];

    for (const candidate of candidates) {
        const normalized = normalizePassedValue(candidate);
        if (normalized != null) return normalized;
    }

    if (step?.result?.success === false || step?.result?.result?.success === false) {
        return false;
    }
    if (step?.details?.error) return false;

    return null;
}

function normalizeRecordingStatus(status) {
    if (!status) return null;
    if (status === 'success') return 'completed';
    if (RECORDING_SAVE_STATUS.has(status)) return status;
    return null;
}

/**
 * Derive persisted recording status for save/load when backend leaves status as pending.
 * Explicit failed markers are kept; args.passed can correct stale pending/completed.
 */
export function resolveRecordingStepStatus(step) {
    if (!step || step.customStep === true) {
        return step?.status || 'pending';
    }

    const normalized = normalizeRecordingStatus(step.status);
    if (normalized === 'failed' || normalized === 'failed_by_evaluation') {
        return normalized;
    }

    const passed = getRecordingStepPassed(step);
    if (passed === false) {
        return 'failed';
    }
    if (passed === true) {
        return 'completed';
    }

    if (normalized === 'completed') {
        return 'completed';
    }

    // Action steps that ran during recording often keep status pending but have a timestamp.
    if (!isEvaluationStep(step) && step.timestamp && !step.details?.error && !step.error) {
        return 'completed';
    }

    return step.status || 'pending';
}

function hasRecordingPersistenceSignal(step) {
    if (!step || step.customStep === true) return false;
    if (typeof step.replayable === 'boolean') return true;
    if (step.timestamp) return true;
    if (getRecordingStepPassed(step) != null) return true;
    if (step.status === 'success' || step.status === 'running') {
        return false;
    }
    const normalized = normalizeRecordingStatus(step.status);
    return (
        normalized === 'failed' ||
        normalized === 'failed_by_evaluation' ||
        normalized === 'completed'
    );
}

/**
 * Derive replayable flag for persisted recording plans.
 * Status-derived replayable wins over stale explicit flags from older saves.
 */
export function resolveRecordingReplayable(step, status) {
    if (step?.customStep === true) return undefined;
    if (status === 'failed' || status === 'failed_by_evaluation') return false;
    if (status === 'completed') return true;
    if (status === 'pending') {
        return hasRecordingPersistenceSignal(step) ? false : undefined;
    }
    if (typeof step?.replayable === 'boolean') return step.replayable;
    return undefined;
}

export function isRecordingSkipMarkerCandidate(step) {
    if (!step || step.customStep === true) return false;
    const status = resolveRecordingStepStatus(step);
    if (status === 'completed') return false;
    if (status === 'failed' || status === 'failed_by_evaluation') return true;
    if (status === 'pending') {
        if (getRecordingStepPassed(step) === false) return true;
        if (step.replayable === false) return true;
    }
    return false;
}

export function getRecordingStepMatchKey(step, fallbackIndex) {
    const stableId = step?.stepId ?? step?.id;
    if (stableId != null && String(stableId).trim()) return `id:${stableId}`;
    return `idx:${step?.stepIndex ?? fallbackIndex}`;
}

/** Apply resolved recording status + replayable to a single step. */
export function normalizeStepRecordingMarkers(step) {
    if (!step) return step;
    if (step.status === 'success' || step.status === 'running') {
        if (!hasRecordingPersistenceSignal(step)) return step;
    }
    if (!hasRecordingPersistenceSignal(step) && step.status === 'pending') {
        return step;
    }
    const status = resolveRecordingStepStatus(step);
    const replayable = resolveRecordingReplayable(step, status);
    return {
        ...step,
        status,
        ...(typeof replayable === 'boolean' ? { replayable } : {}),
    };
}

/** Live-only runtime verdict keys overlayed onto testComplete plan args. */
const LIVE_RUNTIME_ARG_KEYS = [
    'passed',
    'actualOutcome',
    'actualOutcomeRaw',
    'reason',
    'reasonRaw',
    'confidence',
    'confidenceRaw',
    'confidence_reason',
];

/**
 * Prefer testComplete plan args (assertionCode, expectedOutcome, etc.).
 * Overlay live recording outcome fields so run status stays accurate (QPN-1899).
 */
function mergeRecordingStepArgs(liveStep = {}, planStep = {}) {
    const liveArgs = liveStep?.args ?? liveStep?.arguments;
    const planArgs = planStep?.args ?? planStep?.arguments;
    if (planArgs == null && liveArgs == null) return undefined;

    if (planArgs == null || typeof planArgs !== 'object') {
        return liveArgs && typeof liveArgs === 'object' ? { ...liveArgs } : liveArgs;
    }

    const args = { ...planArgs };
    if (liveArgs && typeof liveArgs === 'object') {
        for (const key of LIVE_RUNTIME_ARG_KEYS) {
            if (liveArgs[key] !== undefined && liveArgs[key] !== null) {
                args[key] = liveArgs[key];
            }
        }
    }
    return args;
}

/**
 * Merge live recording stream data with testComplete plan rows before persisting in Redux.
 * Plan row owns recorded fields; live owns run status / outcome chrome.
 */
export function mergeStepRecordingState(liveStep = {}, planStep = {}) {
    const args = mergeRecordingStepArgs(liveStep, planStep);
    const combined = {
        ...planStep,
        ...liveStep,
        ...(args != null ? { args } : {}),
        timestamp: liveStep?.timestamp ?? planStep?.timestamp,
        executionStrategies:
            planStep?.executionStrategies?.length > 0
                ? planStep.executionStrategies
                : liveStep?.executionStrategies,
        stepId: planStep?.stepId ?? liveStep?.stepId,
        passed: liveStep?.passed ?? planStep?.passed,
        status: liveStep?.status ?? planStep?.status,
        // Keep plan-recorded evaluation mode unless user already toggled on the live step.
        useAssertionCode:
            liveStep?.useAssertionCode !== undefined
                ? liveStep.useAssertionCode
                : planStep?.useAssertionCode,
    };
    return normalizeStepRecordingMarkers(combined);
}

export function reconcileRecordingPlanInstructions(planInstructions = [], liveInstructions = []) {
    const liveFlat = [];
    liveInstructions.forEach((inst) => {
        (inst.aiSteps ?? []).forEach((step) => liveFlat.push(step));
    });
    const liveByKey = new Map();
    liveFlat.forEach((step, index) => {
        liveByKey.set(getRecordingStepMatchKey(step, index), step);
        if (step?.id != null) {
            liveByKey.set(`sid:${String(step.id)}`, step);
        }
    });

    let globalIndex = 0;
    return planInstructions.map((instruction) => ({
        ...instruction,
        aiSteps: (instruction.aiSteps ?? []).map((step) => {
            const liveStep =
                liveByKey.get(getRecordingStepMatchKey(step, globalIndex)) ??
                (step?.id != null ? liveByKey.get(`sid:${String(step.id)}`) : undefined);
            globalIndex += 1;
            return mergeStepRecordingState(liveStep, step);
        }),
    }));
}

export function normalizeInstructionsRecordingMarkers(instructions = []) {
    return instructions.map((instruction) => ({
        ...instruction,
        aiSteps: (instruction.aiSteps ?? []).map((step) => {
            const normalized = normalizeStepRecordingMarkers(step);
            if (
                step?.status === 'success' &&
                normalized?.status === 'success' &&
                !hasRecordingPersistenceSignal(step)
            ) {
                return { ...normalized, status: 'completed' };
            }
            return normalized;
        }),
    }));
}

/** True when the plan carries recording-time pass/fail markers that should stay visible in the UI. */
export function hasPersistedRecordingOutcome(step) {
    if (!step || step.customStep === true) return false;
    const status = resolveRecordingStepStatus(step);
    return status === 'completed' || status === 'failed' || status === 'failed_by_evaluation';
}

export function instructionsHaveRecordingOutcomes(instructions = []) {
    for (const instruction of instructions || []) {
        for (const step of instruction.aiSteps || []) {
            if (hasPersistedRecordingOutcome(step)) return true;
        }
    }
    return false;
}

/**
 * True only for a recorder session that just ended in this workspace — not when editing a saved plan.
 */
export function shouldConfirmSaveWithFailedRecordingSteps(
    generationRun = {},
    planDraft = {}
) {
    if (generationRun.sessionMode !== 'recorder') return false;
    if (planDraft?.justSavedInSession) return false;

    const status = generationRun.status;
    return (
        status === RUN_STATUS.COMPLETED ||
        status === RUN_STATUS.STOPPED ||
        status === RUN_STATUS.FAILED
    );
}

/**
 * Failed recording steps that will be skipped on replay — used before save confirmation.
 */
export function hasRecordingPlanForSaveConfirm(instructions = [], { sessionMode = null } = {}) {
    if (sessionMode === 'recorder') return true;
    if (sessionMode === 'generate') return false;
    // Saved plans loaded from DB carry recording markers — do not infer an active recording session.
    return false;
}

export function collectFailedRecordingStepsForSave(instructions = [], options = {}) {
    const { sessionMode = null } = options;
    if (!hasRecordingPlanForSaveConfirm(instructions, { sessionMode })) return [];

    const seenStepKeys = new Set();
    return getActiveFlatSteps(instructions)
        .map((step, flatIndex) => ({ step, stepNumber: flatIndex + 1 }))
        .filter(({ step }) => isRecordingSkipMarkerCandidate(step))
        .filter(({ step, stepNumber }) => {
            const key =
                step?.id != null
                    ? `id:${String(step.id)}`
                    : step?.stepId != null
                      ? `sid:${String(step.stepId)}`
                      : `num:${stepNumber}`;
            if (seenStepKeys.has(key)) return false;
            seenStepKeys.add(key);
            return true;
        })
        .map(({ step, stepNumber }) => {
            const description = getReplayStepDisplayDescription(step);
            return {
                stepNumber,
                description: description || `Step ${stepNumber}`,
            };
        });
}

/**
 * Resolve failed recording steps for the save-confirm modal from current Redux state.
 */
export function getFailedRecordingStepsForSaveConfirmFromState(automationStudioNewState) {
    if (!automationStudioNewState) {
        return { shouldPrompt: false, failedSteps: [] };
    }

    const { generationRun, planDraft } = automationStudioNewState;
    if (!shouldConfirmSaveWithFailedRecordingSteps(generationRun, planDraft)) {
        return { shouldPrompt: false, failedSteps: [] };
    }

    const liveInstructions = planDraft?.instructions ?? [];
    const snapshotInstructions =
        generationRun?.status === RUN_STATUS.COMPLETED &&
        Array.isArray(generationRun?.completedInstructions) &&
        generationRun.completedInstructions.length > 0
            ? generationRun.completedInstructions
            : liveInstructions;

    const instructionsForSaveCheck = reconcileRecordingPlanInstructions(
        snapshotInstructions,
        liveInstructions
    );
    const failedSteps = collectFailedRecordingStepsForSave(instructionsForSaveCheck, {
        sessionMode: 'recorder',
    });

    return {
        shouldPrompt: failedSteps.length > 0,
        failedSteps,
    };
}
